import { useState, type MouseEvent, type ReactNode } from "react";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import type { AnalysisOverridePayload } from "@/types/analysisRun";
import type { BorrowerTransaction } from "@/hooks/queries/useBorrowerTransactionsQuery";

/** Matches CorrectionsPanel / All Transactions category options (stored values). */
export const ANALYSIS_TXN_TYPES = [
  "DEPOSIT",
  "MCA_DEPOSIT",
  "MCA_WITHDRAWAL",
  "COLLECTION_PAYMENT",
  "LOC_DRAW",
  "LOC_PAYMENT",
  "LOAN_DEPOSIT",
  "LOAN_PAYMENT",
  "AUTO_LOAN_PAYMENT",
  "EQUIPMENT_PAYMENT",
  "CREDIT_CARD_PAYMENT",
  "SBA_LOAN_PAYMENT",
  "PAYROLL",
  "TAX_PAYMENT",
  "INSURANCE",
  "RENT_LEASE",
  "UTILITY",
  "FUEL",
  "MERCHANT_FEE",
  "OWNER_DRAW",
  "OWNER_INJECTION",
  "PAYROLL_IN",
  "REFUND",
  "INTEREST",
  "TRANSFER_IN",
  "TRANSFER_OUT",
  "NSF_RETURN",
  "FEE",
  "OTHER",
] as const;

/**
 * Frontend-only display names. Stored / API values stay on ANALYSIS_TXN_TYPES
 * so we do not need a data migration.
 */
const CATEGORY_DISPLAY_LABELS: Record<string, string> = {
  MCA_DEPOSIT: "MCA_FUNDING",
  MCA_WITHDRAWAL: "MCA_PAYMENT",
  COLLECTION_PAYMENT: "COLLECTION",
  AUTO_LOAN_PAYMENT: "AUTO_LOAN",
  EQUIPMENT_PAYMENT: "EQUIPMENT",
  CREDIT_CARD_PAYMENT: "CARD_PAYMENT",
  SBA_LOAN_PAYMENT: "SBA_LOAN",
  TAX_PAYMENT: "TAX",
  RENT_LEASE: "RENT",
  OWNER_INJECTION: "OWNER_IN",
};

export function categoryDisplayLabel(stored: string): string {
  return CATEGORY_DISPLAY_LABELS[stored] ?? stored;
}

export function revenueLabel(tx: {
  transaction_type?: string;
  amount: number;
  is_revenue?: boolean;
}): "TR" | "NTR" | "-" {
  if (tx.transaction_type === "debit" || tx.amount < 0) return "-";
  return tx.is_revenue ? "TR" : "NTR";
}

export function saveTransactionClassification(
  onSave: (payload: AnalysisOverridePayload) => void,
  tx: Pick<BorrowerTransaction, "override_key">,
  field: "rev" | "txn_type",
  value: string
): void {
  if (!tx.override_key) return;
  onSave({
    target_type: "transaction",
    target_key: tx.override_key,
    field,
    value,
  });
}

function categoryOptions(current: string): string[] {
  if (ANALYSIS_TXN_TYPES.includes(current as (typeof ANALYSIS_TXN_TYPES)[number])) {
    return [...ANALYSIS_TXN_TYPES];
  }
  return current ? [current, ...ANALYSIS_TXN_TYPES] : [...ANALYSIS_TXN_TYPES];
}

/** Native-select-sized trigger for dense table cells; colours come from the theme. */
const OVERRIDE_TRIGGER_CLASS = "gap-1 px-2 py-1 text-xs shadow-none focus:ring-inset";

/**
 * The row toggles on click. The trigger gets touch and keyboard-activation clicks; the portalled
 * content still bubbles through the React tree, so clicks inside the list must stop there too.
 */
const stopRowToggle = (e: MouseEvent) => e.stopPropagation();

type OverrideSelectProps<T extends string> = {
  label: string;
  value: T | "";
  options: readonly T[];
  renderOption: (option: T) => ReactNode;
  onChange: (next: T) => void;
  triggerClassName: string;
  contentClassName?: string;
  placeholder?: string;
};

function OverrideSelect<T extends string>({
  label,
  value,
  options,
  renderOption,
  onChange,
  triggerClassName,
  contentClassName,
  placeholder,
}: OverrideSelectProps<T>) {
  const [open, setOpen] = useState(false);
  // Radix mounts every item even while closed; tables render one select per row, so closed
  // selects mount only the selected item, which SelectValue needs for its text.
  const shown = open ? options : options.filter((option) => option === value);
  return (
    <Select
      value={value}
      open={open}
      onOpenChange={setOpen}
      onValueChange={(next) => onChange(next as T)}
    >
      <SelectTrigger
        size="sm"
        aria-label={label}
        className={cn(OVERRIDE_TRIGGER_CLASS, triggerClassName)}
        onClick={stopRowToggle}
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className={contentClassName} onClick={stopRowToggle}>
        {shown.map((option) => (
          <SelectItem key={option} value={option} className="text-xs">
            {renderOption(option)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

type CategoryOverrideSelectProps = {
  value: string;
  onChange: (next: string) => void;
};

export function CategoryOverrideSelect({ value, onChange }: CategoryOverrideSelectProps) {
  return (
    <OverrideSelect
      label="Category"
      value={value}
      options={categoryOptions(value)}
      renderOption={categoryDisplayLabel}
      onChange={onChange}
      triggerClassName="w-full max-w-full min-w-0"
      placeholder="—"
    />
  );
}

const REVENUE_OPTIONS = ["TR", "NTR"] as const;
type RevenueOption = (typeof REVENUE_OPTIONS)[number];

function renderRevenueOption(option: RevenueOption) {
  return (
    <span className="flex items-center gap-1">
      <span
        aria-hidden
        className={cn(
          "h-1.5 w-1.5 shrink-0 rounded-full",
          option === "TR" ? "bg-green-500" : "bg-amber-500"
        )}
      />
      {option}
    </span>
  );
}

type RevenueOverrideSelectProps = {
  isRevenue: boolean;
  onChange: (next: RevenueOption) => void;
};

export function RevenueOverrideSelect({ isRevenue, onChange }: RevenueOverrideSelectProps) {
  return (
    <OverrideSelect
      label="Revenue"
      value={isRevenue ? "TR" : "NTR"}
      options={REVENUE_OPTIONS}
      renderOption={renderRevenueOption}
      onChange={onChange}
      // Fits the dashboard's narrow Revenue column with the chevron.
      triggerClassName="w-auto px-1.5 [&>svg]:h-3 [&>svg]:w-3"
      contentClassName="min-w-[5rem]"
    />
  );
}
